import Alpine from 'alpinejs'
import VertiGisCallback from "./VertiGisCallback";
import VertigisSearchUrl from "./VertigisSearchUrlFactory.js";
import VertigisIdentifyUrl from "./VertigisIdentifyUrlFactory.js";
import axios from "axios";
import Choices from "choices.js";
import VertiGisLocationFormatter from "./VertiGisLocationFormatter";

export default class VertiGisMap {
    #element
    #mapView
    #choice
    #vertigisUrlFactory

    static init() {
        let VertiGisCallbackInstance = new VertiGisCallback();
        VertiGisCallbackInstance.initXD();

        Array.from(document.querySelectorAll('.verti-gis-map')).forEach(element => {
            Alpine.store('highlightedMapItem', '')
            Alpine.store('vertigisResults', [])
            Alpine.store('districtFilter', '')
            Alpine.store('sportsTypeFilter', '')
            Alpine.store('vertigisMapView', element.dataset.mapType)
            Alpine.store('formatters', VertiGisLocationFormatter)

            document.addEventListener('alpine:initialized', () => {
                new VertiGisMap(element);
            })
        });
    }

    constructor(element) {
        this.#element = element
        this.#mapView = element.dataset.mapType;
        this.#vertigisUrlFactory = new VertigisSearchUrl(this.#mapView);

        this.#initFilters();

        this.#initChoices();

        this.#updateIframe()

        this.#setupVertigisCallbackEventListeners();

        this.#setupMapAlpineEventListeners();
    }

    #setupMapAlpineEventListeners() {
        window.addEventListener('vertigisSearchTriggered', async (e) => {
            e.preventDefault();
            const vertigisSearchType = e.detail.vertigisSearchType;
            const vertigisSearchQuery = e.detail.vertigisSearchQuery;

            if (vertigisSearchType === 'address') {
                return this.#runAddressSearch(vertigisSearchQuery);
            }

            if (vertigisSearchType === 'by_sprengel') {
                return await this.#runVolksschulSprengelSearch(vertigisSearchQuery);
            }

            if (vertigisSearchType === 'label') {
                return this.#runLabelSearch(vertigisSearchQuery);
            }

            throw 'Unknown Search type'
        })

        document.addEventListener('vertigisDistrictFilterChanged', (e) => {
            this.#resetSidebarHighlightedMapItem();
            this.#vertigisUrlFactory.setDistrictFilter(e.detail)
            this.#updateFilterUrlParameter('districtFilter', e.detail)
            this.#updateIframe()
        })

        document.addEventListener('searchTypeChanged', async (e) => {
            const changedFromType = e.detail.oldValue
            const changedToType = e.detail.newValue;

            if (['by_sprengel', 'address'].includes(changedToType)) {
                await Alpine.nextTick()
                this.#initChoices()

                this.#choice.showDropdown()
            }

            if (['by_sprengel', 'address'].includes(changedFromType)) {
                this.#removeChoice()

                this.#vertigisUrlFactory
                    .setSearchParam('')

                this.#updateIframe()
                this.#resetSidebarHighlightedMapItem();
            }

            if (changedFromType === 'by_sprengel') {
                this.#vertigisUrlFactory.setIdFilters([])
            }

            if (changedToType === 'label') {
                await Alpine.nextTick()
                document.querySelector('.vertigis-map-sidebar .search-input').focus()
            }
        })

        document.addEventListener('vertigisSportsTypeFilterChanged', (e) => {
            this.#vertigisUrlFactory.setSportsTypeFilter(e.detail)
            this.#updateFilterUrlParameter('sportsTypeFilter', e.detail)
            this.#updateIframe()
        })

        document.addEventListener('vertigisKindergartenSeatsAvailableFilterChanged', (e) => {
            this.#resetSidebarHighlightedMapItem();
            this.#vertigisUrlFactory.setKindergartenSeatsAvailableFilter(e.detail)
            this.#updateIframe()
        })

        document.addEventListener('vertigisKindergartenPrivatePublicFilterChanged', (e) => {
            this.#resetSidebarHighlightedMapItem();
            this.#vertigisUrlFactory.setKindergartenPrivateFilter(e.detail.filterKindergardenPrivate)
            this.#vertigisUrlFactory.setKindergartenPublicFilter(e.detail.filterKindergardenPublic)
            this.#updateIframe()
        })

        document.addEventListener('vertigisToiletCostsFreeFilterChanged', (e) => {
            this.#resetSidebarHighlightedMapItem();
            this.#vertigisUrlFactory.setToiletCostsFreeFilter(e.detail)
            this.#updateIframe()
        })

        document.addEventListener('vertigisToiletBarrierFreeFilterChanged', (e) => {
            this.#resetSidebarHighlightedMapItem();
            this.#vertigisUrlFactory.setToiletBarrierFreeFilter(e.detail)
            this.#updateIframe()
        })

        document.addEventListener('sidebarItemSelected', (e) => {
            if (Alpine.store('highlightedMapItem') === e.detail) {
                return
            }

            if (this.#choice) {
                this.#choice.destroy()
                this.#choice.init()
                this.#vertigisUrlFactory.setSearchParam('')
            }

            Alpine.store('highlightedMapItem', e.detail)
            this.#vertigisUrlFactory
                .setSearchId(e.detail)

            this.#updateIframe(false)

            this.#vertigisUrlFactory
                .setSearchId(null)
                .setScale(null)
        })
    }

    #removeChoice() {
        if (this.#choice) {
            this.#choice.passedElement.element.remove()
            this.#choice.destroy()
        }
    }

    #setupVertigisCallbackEventListeners() {
        // sidebar list updates via multilayer search callback via zoom/pan in map
        window.addEventListener('vertigisLocationsUpdated', (e) => {
            const hydratedLocations = e.detail.map(location => window.vertiGisLocations.find(itemLocation => itemLocation.ID === location.ID) || location)

            Alpine.store('vertigisResults', hydratedLocations)
        })

        // maptip callback via click on item in the map
        window.addEventListener('vertigisMapttipResult', async (e) => {
            const coordinates = e.detail;

            const itemId = await this.#getItemIdFromCoordinates(coordinates)

            if (!itemId) {
                return
            }

            const sidebarItem = this.#element.querySelector('#vertigis-sidebar-item-' + itemId)
            if (!sidebarItem) {
                this.#resetSidebarHighlightedMapItem();

                return
            }

            sidebarItem.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'start'})
            Alpine.store('highlightedMapItem', itemId)
        })
    }

    #resetSidebarHighlightedMapItem() {
        Alpine.store('highlightedMapItem', '')
    }

    #runLabelSearch(vertigisSearchQuery) {
        this.#vertigisUrlFactory
            .setSearchParam(vertigisSearchQuery)
            .setSearchType('label')

        this.#updateIframe()
    }

    async #runVolksschulSprengelSearch(vertigisSearchQuery) {
        const choiceJsValue = vertigisSearchQuery.value;

        // first we run a search with the given address as param to find out which volksschul-id(s) this address belongs to
        this.#vertigisUrlFactory
            .setSearchParam(choiceJsValue)
            .setSearchType('by_sprengel')
        let vsIds = await this.#getVsIdsForLivingAddress(this.#vertigisUrlFactory.getDataUrl());

        // then we create a new query where we only filter for the volksschul-id(s) returned in the first step
        this.#vertigisUrlFactory = new VertigisSearchUrl(this.#mapView);
        this.#vertigisUrlFactory.setIdFilters(vsIds)

        this.#updateIframe()
    }

    #runAddressSearch(vertigisSearchQuery) {
        this.#resetSidebarHighlightedMapItem();

        const choiceJsValue = vertigisSearchQuery.value;

        this.#vertigisUrlFactory
            .setDistrictFilter(null)
            .setSearchParam(choiceJsValue)
            .setSearchType('exact_address')

        this.#updateIframe()

        this.#vertigisUrlFactory
            .setScale(null)
    }

    async #getVsIdsForLivingAddress(sprengelSearchUrl) {
        return await axios.get(sprengelSearchUrl)
            .then(response => {
                if (!response.data?.FEATURES) {
                    console.log('no results found for this sprengel')
                    return;
                }

                // VS ids
                return [...new Set(response.data.FEATURES.COORDS.map(coord => coord.search_result[0].column_value))]
            })
    }

    #updateIframe(updateSidebar = true) {
        this.#element.querySelector('.verti-gis-iframe').setAttribute('src', this.#vertigisUrlFactory.getMapUrl())

        if (!updateSidebar) {
            return
        }

        axios.get(this.#vertigisUrlFactory.getDataUrl())
            .then(response => {
                if (!response.data?.FEATURES) {
                    Alpine.store('vertigisResults', [])
                    return;
                }

                let locationIds = response.data.FEATURES.COORDS.map(coord => coord.search_result[0].column_value);

                const hydratedLocations = locationIds
                    .map(dataLocationId => window.vertiGisLocations.find(itemLocation => itemLocation.ID === dataLocationId))
                    .filter(Boolean)

                Alpine.store('vertigisResults', hydratedLocations)
            })
    }

    async #getItemIdFromCoordinates(coordinates) {
        let identifyUrl = (new VertigisIdentifyUrl).getUrl(this.#mapView, coordinates);
        return axios.get(identifyUrl)
            .then(response => {

                let itemId = response.data?.FEATURES?.COORDS[0]?.search_result[0]?.column_value;
                if (!itemId) {
                    return
                }

                return itemId
            })
    }

    #initChoices() {
        let addressSearchInput = document.querySelector('.address-search-input');

        if (!addressSearchInput) {
            return
        }

        this.#choice = new Choices(addressSearchInput, {
            itemSelectText: "", searchResultLimit: 200, noResultsText: "Keine Resultate, bitte Suchbegriff ändern!", searchFloor: 3, choices: [], shouldSort: false, position: 'bottom'
        })

        let myTimeOut = null;
        addressSearchInput.addEventListener('search', async (e) => {
            clearTimeout(myTimeOut)

            myTimeOut = setTimeout(async () => {
                const addressChoices = await this.#getAddressChoicesFromSearchString(e.detail.value)

                if (!addressChoices) {
                    return
                }

                this.#choice.setChoices(addressChoices, 'value', 'label', true)
            }, 500)
        })

        addressSearchInput.addEventListener('choice', (e) => {
            if (e.target.classList.contains('sprengel-search')) {
                this.#runVolksschulSprengelSearch(e.detail.choice)

                return
            }

            this.#runAddressSearch(e.detail.choice);
        })

        window.addEventListener('clearAddressSearch', () => {
            this.#choice.destroy()
            this.#choice.init()

            this.#vertigisUrlFactory
                .setDistrictFilter(null)
                .setSearchParam('')

            this.#updateIframe(false)
        })

        window.addEventListener('clearSprengelAddressSearch', () => {
            this.#choice.destroy()
            this.#choice.init()

            this.#vertigisUrlFactory
                .setDistrictFilter(null)
                .setIdFilters([])
                .setSearchParam('')

            this.#updateIframe()
        })
    }

    async #getAddressChoicesFromSearchString(addressQuery) {
        let VertigisSearchUrlFactory = new VertigisSearchUrl(this.#mapView);
        VertigisSearchUrlFactory.setSearchType('loose_address')
        VertigisSearchUrlFactory.setSearchParam(addressQuery)

        return await axios.get(VertigisSearchUrlFactory.getDataUrl())
            .then(result => {
                if (!result.data?.FEATURES) {
                    console.error('could not find any matching addresses', result.data)

                    return
                }

                return result.data.FEATURES.COORDS.map((coord) => {
                    return {
                        label: coord.search_result[0].column_value, value: coord.search_result[0].column_value
                    }
                })
            });
    }

    #initFilters() {
        this.#element.querySelectorAll('.verti-gis-map-filter select').forEach((filterInput) => {
            const filterType = filterInput.getAttribute('data-filter-type')

            if (!['districtFilter', 'sportsTypeFilter'].includes(filterType)) {
                return
            }

            const urlParams = new URLSearchParams(window.location.search)

            if (!urlParams.has(filterType)) {
                return
            }

            const filterValue = urlParams.get(filterType)

            if (!this.#element.querySelector(`select[data-filter-type=${filterType}] option[value='${filterValue}']`)) {
                return
            }

            if (filterType === 'districtFilter') {
                this.#vertigisUrlFactory.setDistrictFilter(filterValue)
            } else if (filterType === 'sportsTypeFilter') {
                this.#vertigisUrlFactory.setSportsTypeFilter(filterValue)
            }

            Alpine.store(filterType, filterValue)
        })
    }

    #updateFilterUrlParameter(filterType, filterValue) {
        const urlParams = new URLSearchParams(window.location.search)

        if (!filterValue) {
            urlParams.delete(filterType)
        } else {
            urlParams.set(filterType, filterValue)
        }

        window.history.replaceState('', '', `?${urlParams.toString()}`)
    }
}
